import { Edge } from 'reactflow';
import { FTANode, FTANodeType } from '../../types/fta.types';

// Simple tree layout algorithm since we want to avoid heavy dependencies like dagre
export const getLayoutedElements = (nodes: FTANode[], edges: Edge[]) => {
  const nodeWidth = 150;
  const nodeHeight = 120;
  
  // Build adjacency list
  const childrenMap: Record<string, string[]> = {};
  const parentsMap: Record<string, string[]> = {};
  
  nodes.forEach(n => {
    childrenMap[n.id] = [];
    parentsMap[n.id] = [];
  });

  edges.forEach(edge => {
    if (childrenMap[edge.source]) childrenMap[edge.source].push(edge.target);
    if (parentsMap[edge.target]) parentsMap[edge.target].push(edge.source);
  });

  // Find root(s)
  const roots = nodes.filter(n => parentsMap[n.id].length === 0);
  
  // Assign levels (BFS)
  const levels: Record<string, number> = {};
  const queue: {id: string, level: number}[] = roots.map(r => ({ id: r.id, level: 0 }));
  const visited = new Set<string>();

  let maxLevel = 0;
  while (queue.length > 0) {
    const { id, level } = queue.shift()!;
    if (visited.has(id)) continue;
    visited.add(id);
    levels[id] = level;
    maxLevel = Math.max(maxLevel, level);

    const children = childrenMap[id] || [];
    children.forEach(childId => {
      queue.push({ id: childId, level: level + 1 });
    });
  }

  // Assign X positions
  // Group nodes by level
  const nodesByLevel: Record<number, string[]> = {};
  nodes.forEach(n => {
    const lvl = levels[n.id] ?? 0;
    if (!nodesByLevel[lvl]) nodesByLevel[lvl] = [];
    nodesByLevel[lvl].push(n.id);
  });

  const positions: Record<string, {x: number, y: number}> = {};

  // Simple centering strategy
  Object.keys(nodesByLevel).forEach(lvlStr => {
    const lvl = parseInt(lvlStr);
    const nodesInLevel = nodesByLevel[lvl];
    const totalWidth = nodesInLevel.length * nodeWidth;
    const startX = -(totalWidth / 2);
    
    nodesInLevel.forEach((nodeId, index) => {
      positions[nodeId] = {
        x: startX + (index * nodeWidth) + (nodeWidth / 2),
        y: lvl * nodeHeight
      };
    });
  });

  return {
    nodes: nodes.map(node => ({
      ...node,
      position: positions[node.id] || { x: 0, y: 0 }
    })),
    edges
  };
};

// Probability Rollup Calculation
export const calculateProbabilities = (nodes: FTANode[], edges: Edge[]): FTANode[] => {
  // Topological sort or recursive calculation
  // We need to calculate from bottom (leaves) to top
  // But since we have the full graph, we can just do a recursive solver with memoization
  
  const nodeMap = new Map(nodes.map(n => [n.id, n]));
  const childEdgesMap: Record<string, string[]> = {};
  
  edges.forEach(e => {
    if (!childEdgesMap[e.source]) childEdgesMap[e.source] = [];
    childEdgesMap[e.source].push(e.target);
  });

  const memo = new Map<string, number>();

  const getProbability = (nodeId: string): number => {
    if (memo.has(nodeId)) return memo.get(nodeId)!;

    const node = nodeMap.get(nodeId);
    if (!node) return 0;

    // Base case: Basic Event has explicit probability
    if (node.data.type === FTANodeType.BASIC_EVENT && node.data.probability !== undefined) {
      memo.set(nodeId, node.data.probability);
      return node.data.probability;
    }

    const childIds = childEdgesMap[nodeId] || [];
    if (childIds.length === 0) {
      // Leaf node without probability (e.g. undeveloped or incorrectly configured basic event)
      // Return 0 or existing prob if set
      return node.data.probability || 0;
    }

    const childProbs = childIds.map(getProbability);

    let result = 0;
    if (node.data.type === FTANodeType.OR_GATE || node.data.type === FTANodeType.TOP_EVENT || node.data.type === FTANodeType.INTERMEDIATE_EVENT) {
      // OR Gate: P = 1 - product(1 - Pi) (approximation for independent events)
      // Actually Top Event and Intermediate often act as OR if not specified, but usually are fed by a Gate. 
      // If an Event has children directly without a Gate in between, it implies logic. 
      // STRICTLY: Events shouldn't have children directly in FTA, they should connect to Gates. 
      // But if they do, we treat as OR for safety.
      // Correct FTA: Event -> Gate -> Event.
      
      const complementProduct = childProbs.reduce((acc, p) => acc * (1 - p), 1);
      result = 1 - complementProduct;
    } else if (node.data.type === FTANodeType.AND_GATE) {
      // AND Gate: P = product(Pi) (independent events)
      result = childProbs.reduce((acc, p) => acc * p, 1);
    } else {
      // Fallback
      result = 0;
    }

    memo.set(nodeId, result);
    return result;
  };

  // Calculate for all nodes
  nodes.forEach(n => getProbability(n.id));

  // Return new nodes with updated calculatedProbability
  return nodes.map(n => ({
    ...n,
    data: {
      ...n.data,
      calculatedProbability: memo.get(n.id)
    }
  }));
};
